// ACȚIUNILE pe soluții, o singură dată pentru meniul vizualizatorului, pentru
// comutator și pentru pagina «Soluțiile proiectului» (contract §5).
//
// Regula de după orice acțiune: se navighează pe rândul din RĂSPUNS
// (`solutie.id`), nu pe soluția implicită — regula ar arăta schița cât
// Soluția 1 rulează (contract §4, «Cum folosește UI-ul regula»). Registrul se
// recitește întreg ÎNAINTE de navigare, ca ecranul să găsească rândul nou.
//
// Tot ce șterge ceva spune ÎNTÂI ce ia: cascada vine de la server
// (`…/cascada`), iar cât ruta lipsește, din aceeași regulă aplicată pe
// registru (`cascadaLocala`). Asta ține și pentru pornirile care șterg:
// «Reia schița» (S0 cu tot ce stă pe el), «Rerulează» (detaliul) și
// «Pornește calculul detaliat» peste un detaliu existent (contract §5.1).
// «Lanțul de calcul» pornește tot de aici — un al doilea drum, fără
// confirmare, ștergea dintr-un clic toate soluțiile unei arhitecturi.

import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, promoteSolution, startJob, stopJob, type JobOptions } from "../api";
import { t } from "../i18n";
import { cascada as cereCascada, pornesteStructura, stergeSolutia,
         type ActiuneCascada, type Cascada, type Lucrare, type PornireStructura,
         type SolutieRand } from "./api";
import { confirmaPeArhitecturaVeche } from "./PlanseSchimbate";
import { useRegistru } from "./Registru";
import { cascadaLocala, caleRand, caleSolutii, citesteRuta, textCascada } from "./selectie";

export interface Actiuni {
  /** cheia acțiunii care așteaptă răspunsul (una singură deodată) */
  ocupat: string | null;
  eroare: string | null;
  curataEroarea: () => void;
  /** «Pornește calculul detaliat» pe o Soluție N (D9: și când macro nu
   *  trece); peste un detaliu existent, după confirmarea cascadei */
  pornesteDetaliat: (macro: SolutieRand) => Promise<void>;
  /** «Generează structura» pe o arhitectură */
  genereazaSolutia: (arhitecturaId: string) => Promise<void>;
  /** «Importă structura» pe o arhitectură: geometria macro se citește din
   *  planșele ei (`sursa = structurist`), lângă cea generată */
  importaStructura: (arhitecturaId: string) => Promise<void>;
  /** «Reia schița»: S0 nou, cu tot ce stă pe el șters */
  reiaSchita: (arhitecturaId: string) => Promise<void>;
  /** «Rerulează» geometria macro a unei Soluții N, pe același rând */
  reruleaza: (macro: SolutieRand) => Promise<void>;
  /** ■ pe rularea deschisă a rândului */
  opreste: (rand: SolutieRand) => Promise<void>;
  /** ■ pe o lucrare de după etaje (recompartimentarea, fațada, subsolul) */
  opresteLucrarea: (l: Lucrare) => Promise<void>;
  /** «Șterge»: rândul și tot ce stă sub el, după confirmarea cascadei */
  sterge: (rand: SolutieRand, dupa?: string) => Promise<void>;
  /** «Refă excavația» (14.10.2026): planul de situație se reia COMPLET — soluția
   *  și tot ce stă pe ea se șterg (cascada, cu confirmare), apoi pornește jobul
   *  planului, care așază din nou corpurile și excavația pe module */
  refaExcavatia: (plan: SolutieRand) => Promise<void>;
  /** ★ la plan de situație și arhitectură */
  promoveaza: (rand: SolutieRand) => Promise<void>;
  /** o rulare de plan de situație / arhitectură (varianta, importul, subsolul) */
  pornesteJob: (cheie: string, kind: string, opt?: JobOptions) => Promise<void>;
  /** cascada unei acțiuni, cerută serverului, cu rezerva locală */
  cascada: (sid: string, actiune: ActiuneCascada) => Promise<Cascada>;
}

const mesaj = (e: unknown, rezerva: string) =>
  e instanceof ApiError && e.message ? e.message : rezerva;

export function useActiuni(): Actiuni {
  const { projectId, registru, reincarca, reciteste } = useRegistru();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [ocupat, setOcupat] = useState<string | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);

  const cascada = useCallback(async (sid: string, actiune: ActiuneCascada) => {
    try {
      return await cereCascada(projectId, sid, actiune);
    } catch {
      if (!registru) throw new Error(t("actiune.esec"));
      return cascadaLocala(registru, sid, actiune);
    }
  }, [projectId, registru]);

  /** Confirmarea unei acțiuni care șterge: ce ia, în cuvinte. Fără nimic de
   *  luat nu se întreabă nimic. */
  const confirmaCascada = useCallback(async (sid: string, actiune: ActiuneCascada,
                                              cheie: "cascada.confirma_sterge"
                                                | "cascada.confirma_schita"
                                                | "cascada.confirma_reruleaza"
                                                | "cascada.confirma_detaliu"
                                                | "cascada.confirma_refa_excavatia") => {
    const c = await cascada(sid, actiune);
    if (!c.ids.length) return true;
    const ce = registru ? textCascada(registru, c, sid) : String(c.ids.length);
    return window.confirm(t(cheie, { ce, vii: c.rulari_vii }));
  }, [cascada, registru]);

  const porneste = useCallback(async (cheie: string, corp: PornireStructura) => {
    setEroare(null);
    setOcupat(cheie);
    try {
      const r = await pornesteStructura(projectId, corp);
      await reciteste();
      // pe același rând (o rerulare) rămâi pe vederea în care erai; pe un rând
      // nou, corpul ales rămâne — codurile corpurilor sunt aceleași pe S0, M și D
      const ruta = citesteRuta(pathname, search);
      if (r?.solutie?.id && r.solutie.id !== ruta.sol) {
        navigate(caleRand(projectId, r.solutie,
                          { corp: ruta.pagina === "structura" ? ruta.corp : null }));
      }
    } catch (e) {
      // «Generează soluția» pe o arhitectură care are deja una generată: serverul
      // spune care, iar ecranul merge pe ea în loc să arate o eroare
      if (e instanceof ApiError && e.code === "exista_solutie_generata") {
        const s = (e.date as { solutie?: SolutieRand } | undefined)?.solutie;
        if (s?.id) { navigate(caleRand(projectId, s)); return; }
      }
      setEroare(mesaj(e, t("actiune.esec_pornire")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reciteste, navigate, pathname, search]);

  const pornesteDetaliat = useCallback(async (macro: SolutieRand) => {
    const areDetaliu = !!registru?.solutii.some(
      (s) => s.faza === "detaliu" && s.parent_solution_id === macro.id);
    if (areDetaliu) {
      try {
        if (!(await confirmaCascada(macro.id, "detaliu", "cascada.confirma_detaliu"))) return;
      } catch (e) {
        setEroare(mesaj(e, t("actiune.esec")));
        return;
      }
    }
    await porneste(`detaliu:${macro.id}`,
                   { kind: "structure", faza: "detaliu", parent_solution_id: macro.id });
  }, [porneste, confirmaCascada, registru]);

  // Structura pornită pe o arhitectură importată din planșe care s-au
  // schimbat de atunci nu pleacă tăcut: omul alege între ea și reimport.
  // AICI, nu pe butoane: «Generează soluția» stă pe cinci ecrane.
  const peArhitecturaVeche = useCallback((arh: string) =>
    confirmaPeArhitecturaVeche(registru?.solutii.find((s) => s.id === arh)),
  [registru]);

  const genereazaSolutia = useCallback(async (arh: string) => {
    if (!peArhitecturaVeche(arh)) return;
    await porneste(`genereaza:${arh}`,
                   { kind: "structure", faza: "macro", upstream_solution_id: arh });
  }, [porneste, peArhitecturaVeche]);

  const importaStructura = useCallback(async (arh: string) => {
    if (!peArhitecturaVeche(arh)) return;
    await porneste(`importa:${arh}`,
                   { kind: "structure", faza: "macro", upstream_solution_id: arh,
                     import_structura: true });
  }, [porneste, peArhitecturaVeche]);

  const reiaSchita = useCallback(async (arh: string) => {
    if (!peArhitecturaVeche(arh)) return;
    try {
      if (!(await confirmaCascada(arh, "schita", "cascada.confirma_schita"))) return;
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec")));
      return;
    }
    await porneste(`schita:${arh}`,
                   { kind: "structure", faza: "schita", upstream_solution_id: arh });
  }, [porneste, confirmaCascada, peArhitecturaVeche]);

  const reruleaza = useCallback(async (macro: SolutieRand) => {
    try {
      if (!(await confirmaCascada(macro.id, "reruleaza", "cascada.confirma_reruleaza"))) return;
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec")));
      return;
    }
    await porneste(`reruleaza:${macro.id}`,
                   { kind: "structure", faza: "macro", solution_id: macro.id });
  }, [porneste, confirmaCascada]);

  const opreste = useCallback(async (rand: SolutieRand) => {
    if (!rand.rulare) return;
    setEroare(null);
    setOcupat(`opreste:${rand.id}`);
    try {
      await stopJob(projectId, rand.rulare.job_id);
      await reincarca();
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec_oprire")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reincarca]);

  const opresteLucrarea = useCallback(async (l: Lucrare) => {
    setEroare(null);
    setOcupat(`opreste:${l.job_id}`);
    try {
      await stopJob(projectId, l.job_id);
      await reincarca();
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec_oprire")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reincarca]);

  const sterge = useCallback(async (rand: SolutieRand, dupa?: string) => {
    setEroare(null);
    try {
      if (!(await confirmaCascada(rand.id, "sterge", "cascada.confirma_sterge"))) return;
      setOcupat(`sterge:${rand.id}`);
      await stergeSolutia(projectId, rand.id);
      await reciteste();
      if (dupa !== undefined) navigate(dupa || caleSolutii(projectId));
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec_stergere")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reciteste, navigate, confirmaCascada]);

  const refaExcavatia = useCallback(async (plan: SolutieRand) => {
    setEroare(null);
    try {
      if (!(await confirmaCascada(plan.id, "sterge", "cascada.confirma_refa_excavatia"))) return;
      setOcupat(`excavatie:${plan.id}`);
      await stergeSolutia(projectId, plan.id);
      await startJob(projectId, "site_plan");
      await reciteste();
      navigate(caleSolutii(projectId));
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec_pornire")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reciteste, navigate, confirmaCascada]);

  const promoveaza = useCallback(async (rand: SolutieRand) => {
    setEroare(null);
    setOcupat(`stea:${rand.id}`);
    try {
      await promoteSolution(projectId, rand.id);
      await reciteste();
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec_favorita")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reciteste]);

  const pornesteJob = useCallback(async (cheie: string, kind: string, opt?: JobOptions) => {
    setEroare(null);
    setOcupat(cheie);
    try {
      await startJob(projectId, kind, opt);
      await reciteste();
    } catch (e) {
      setEroare(mesaj(e, t("actiune.esec_pornire")));
    } finally {
      setOcupat(null);
    }
  }, [projectId, reciteste]);

  return {
    ocupat, eroare, curataEroarea: () => setEroare(null),
    pornesteDetaliat, genereazaSolutia, importaStructura, reiaSchita, reruleaza, opreste,
    opresteLucrarea,
    sterge, refaExcavatia, promoveaza, pornesteJob, cascada,
  };
}
